import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, ArrowRight, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { useCurrency } from '@/lib/useCurrency';
import { HelpBubble } from '@/components/HelpBubble';

const TYPE_TONE = {
  investigate: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
  review: 'bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-200',
  opportunity: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200',
  emerging_trend: 'bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200',
  monitor: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
};

function formatValue(v, cs) {
  if (v === null || v === undefined) return null;
  if (v >= 1_000_000) return `${cs}${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${cs}${(v / 1_000).toFixed(0)}K`;
  return `${cs}${Math.round(v).toLocaleString()}`;
}

export default function SignalsWidget() {
  const [data, setData] = useState({ counts: {}, top: [], gated: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem('signalsWidgetExpanded') === '1'; } catch { return false; }
  });
  const toggleExpanded = () => setExpanded((v) => {
    const n = !v;
    try { localStorage.setItem('signalsWidgetExpanded', n ? '1' : '0'); } catch { /* noop */ }
    return n;
  });
  const { symbol: cs } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/signals/stats');
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (error) return null;
  if (data.gated) {
    return (
      <Card className="border-dashed" data-testid="signals-widget-gated">
        <CardContent className="p-4 flex items-center gap-3">
          <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Signal Intelligence</span>
              <Badge variant="outline" className="text-[10px] uppercase">Premium</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              A ranked weekly feed of what to investigate — concentration shifts, price variance, contract leakage and more. Ask your platform admin to enable Signal Intelligence for this organization.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  const total = data.counts?.new ?? data.counts?.total ?? 0;

  return (
    <Card data-testid="signals-widget">
      <CardHeader
        className="py-3 cursor-pointer select-none hover:bg-muted/40 transition-colors"
        onClick={toggleExpanded}
        data-testid="signals-widget-toggle"
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Search className="w-4 h-4" /> This week's investigation priorities
            <span onClick={(e) => e.stopPropagation()}>
              <HelpBubble
                text="Only brand-new, unactioned signals appear here — once you acknowledge, investigate, validate or dismiss a signal in the Signal Intelligence tab, it drops off this preview. Signals come from seven detectors (supplier concentration, price variance, contract leakage, tail spend, duplicates, category drift). Click this header to expand or collapse — your choice is remembered on this browser."
                side="bottom"
              />
            </span>
            {!loading && total > 0 && (
              <Badge variant="secondary" className="text-[10px]" data-testid="signals-widget-count">{total}</Badge>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); navigate('/?tab=signals'); }}
            data-testid="signals-widget-view-all"
          >
            View all <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      {expanded && (
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : total === 0 ? (
          <div className="text-sm text-muted-foreground">
            No new signals — everything flagged so far has been actioned. Open the Signal Intelligence tab to review in-progress items.
          </div>
        ) : (
          <ul className="space-y-2">
            {(data.top || []).map((s) => (
              <li
                key={s.id}
                className="flex items-start gap-2 py-2 border-b last:border-0 cursor-pointer hover:bg-muted/40 rounded px-1"
                onClick={() => navigate('/?tab=signals')}
                data-testid={`widget-signal-${s.id}`}
              >
                <Badge className={`text-[10px] uppercase ${TYPE_TONE[s.signal_type] || ''} border-0 mt-0.5`}>
                  {s.signal_type.replace('_', ' ')}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.title}</div>
                </div>
                {s.value_at_stake ? (
                  <span className="text-xs font-semibold whitespace-nowrap">
                    {formatValue(s.value_at_stake, cs)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      )}
    </Card>
  );
}
